/**
 * One warehouse report at the stage chosen in the URL (`?stage=raw|condition`), drawn by that desk's
 * own page: RSW's ReportPage, CSW's CswReportPage. Filters live in the URL so it can be bookmarked.
 */
import {
  openStages,
  warehouseStageReport,
  type WarehouseReportStage,
} from "@nexseed/shared";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useAuth } from "@/features/auth/store";
import { cswReportSearch } from "@/features/warehouse/reports/csw-api";
import { CswReportPage } from "@/features/warehouse/reports/csw-reports-pages";
import { ReportPage } from "@/features/warehouse/reports/reports-pages";
import { StageReportSwitch } from "@/features/warehouse/reports/stage-reports-pages";
import { useDeskFacility } from "@/features/warehouse/route-helpers";

const search = cswReportSearch.extend({
  stage: z.enum(["raw", "condition"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/reports/warehouse/$slug")({
  validateSearch: search,
  // The report's code at any stage it covers opens it (user, 2026-10-16).
  beforeLoad: ({ params }) => {
    const r = warehouseStageReport(params.slug);
    if (!r || !openStages(r, useAuth.getState().can).length)
      throw redirect({ to: "/reports/warehouse" });
  },
  component: Page,
});

function Page() {
  const { slug } = Route.useParams();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const can = useAuth((st) => st.can);
  const facilityId = useDeskFacility();
  const r = warehouseStageReport(slug);
  if (!r) return null;
  const stages = openStages(r, can);
  // A stage this login may not open (or the report lacks) falls back to the first it may.
  const stage: WarehouseReportStage | undefined =
    s.stage && stages.includes(s.stage) ? s.stage : stages[0];
  if (!stage) return null;
  const { stage: _ignored, ...filters } = s;
  const onSearch = (next: Partial<typeof s>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  // Switching desk starts its filters afresh: the two desks' filters differ.
  const onStage = (next: WarehouseReportStage) =>
    void navigate({ search: { stage: next } });
  return (
    <>
      <StageReportSwitch stages={stages} value={stage} onChange={onStage} />
      {stage === "raw" && r.stages.raw ? (
        <ReportPage
          stage="raw"
          reportKey={r.stages.raw}
          facilityId={facilityId}
          search={filters}
          onSearch={onSearch}
        />
      ) : r.stages.condition ? (
        <CswReportPage
          reportKey={r.stages.condition}
          facilityId={facilityId}
          search={filters}
          onSearch={onSearch}
        />
      ) : null}
    </>
  );
}
